import React, { useEffect, useRef } from 'react';
import { Monitor, X } from 'lucide-react';
import { useBrowserRecommendation } from '../../hooks/useBrowserRecommendation';
import { BROWSER_RECOMMENDATION_MESSAGE } from '../../utils/browserRecommendation';
import { Button } from './button';

function setBannerVisible(visible) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('browser-recommendation-visible', visible);
}

/**
 * Non-blocking, dismissible banner recommending Chrome on laptop/desktop.
 * Mounted globally from App.js.
 */
export default function BrowserRecommendationBanner() {
  const { visible, dismiss } = useBrowserRecommendation();
  const bannerRef = useRef(null);

  useEffect(() => {
    setBannerVisible(visible);
    return () => {
      setBannerVisible(false);
    };
  }, [visible]);

  useEffect(() => {
    const el = bannerRef.current;
    if (!visible || !el || typeof ResizeObserver === 'undefined') {
      if (typeof document !== 'undefined') {
        document.documentElement.style.removeProperty('--browser-recommendation-banner-h');
      }
      return undefined;
    }

    const syncBannerHeight = () => {
      document.documentElement.style.setProperty(
        '--browser-recommendation-banner-h',
        `${el.offsetHeight}px`,
      );
    };

    syncBannerHeight();
    const observer = new ResizeObserver(syncBannerHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--browser-recommendation-banner-h');
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Browser recommendation"
      className="fixed top-0 left-0 right-0 z-[110] border-b border-slate-200/90 bg-gradient-to-r from-slate-50 to-blue-50/90 shadow-sm dark:border-gray-700 dark:from-gray-900 dark:to-gray-900"
    >
      <div className="safe-area-top flex min-h-11 items-start gap-2 px-3 py-1.5 sm:min-h-[48px] sm:items-center sm:gap-3 sm:px-4 sm:py-2.5">
        <Monitor
          className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400 sm:mt-0"
          aria-hidden="true"
        />
        <p
          className="min-w-0 flex-1 text-ds-body leading-snug text-slate-700 dark:text-gray-200"
          aria-live="polite"
        >
          {BROWSER_RECOMMENDATION_MESSAGE}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={dismiss}
          className="h-9 w-9 shrink-0 text-slate-500 hover:bg-slate-200/80 hover:text-slate-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          aria-label="Dismiss browser recommendation for 30 days"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
